import React from 'react'
import _ from 'lodash'
import moment from 'moment'
import { Toast, Loader } from '@vzmi/shp_shared_compo'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label
} from 'recharts'
import ReactResizeDetector from 'react-resize-detector'
import { NoData } from '../../../Common/StatePage'
import SPBaseModule from '../../../../lib/SPBaseModule'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import { DATE_FORMAT, getFormatData, TIME_DURATION } from '../../../../lib/MomentHelper'
import { dollarFormat, getNumeralFormat } from '../../../../../lib/renderUtil'
import ComposeChartModule from '../../../Common/NewDashboard/Chart/BaseChartModule/ComposeChart/ComposeChart'
import QuantityAmountTooltip from '../../../Common/NewDashboard/Chart/ToolTip/QuantityAmountTooltip/QuantityAmountTooltip'
import CustomizeXAxisTick from './CustomizeXAxisTick'
import YAxisTick from '../../../Common/NewDashboard/Chart/YAxisTick/YAxisTick'
import CustomizedLegend from '../../../Common/NewDashboard/Chart/Legend/Legend'
import YAxisLabel from '../../../Common/NewDashboard/Chart/YAxisLabel/YAxisLabel'
import LineDot from '../../../Common/NewDashboard/Chart/LineDot/LineDot'
import styles from './SaleDailyChart.css'
import { CARRIER } from '../constants/carrier'
import { transformPageViewsListToChartDataList } from '../Shared'
import TooltipIcon from '../../../Common/TooltipIcon/TooltipIcon'
import { ALL_CARRIERS } from '../PageViewsAnalysis/PageViewsDaily/PageViewsDaily'

const DEFAULT_TIME_DURATION = TIME_DURATION.DAY.value

export const FIELD = {
  REPORT_DATE: 'reportDate',
  AMOUNT: 'amount',
  QUANTITY: 'quantity',
  MAIN_RANGE: 'mainRange'
}

// 訂購概況合併瀏覽量分析資料
export const mergeSaleData = (data, pageViewsData) => {
  // concat the 2nd array
  // group by the identical key
  // left currey merge to to create a new empty object, and spread the group as parameters
  const result = _.flow(
    () => _.concat(data, pageViewsData),
    concatData => _.groupBy(concatData, 'reportDate'),
    groupByData => _.map(groupByData, _.spread(_.curry(_.merge, {})))
  )()
  return result
}

class SaleDailyChartContainer extends SPBaseModule {
  constructor (props) {
    super(props)

    this.state = {
      data: []
    }
  }

  fetchData = async (start, end) => {
    const [
      reportDateStart, reportDateEnd
    ] = [moment(start), moment(end)].map(momentDate => getFormatData(momentDate, DATE_FORMAT.API))
    const fields = ['+newOrder']
    const params = { reportDateStart, reportDateEnd, fields }
    const { fetchSaleStatsByDate, fetchAllListingPageViews } = this.props
    // 訂單
    const [error, payload] = await safeAwait(
      fetchSaleStatsByDate(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }

    // 瀏覽量
    // 需轉換同瀏覽量分析，預設全部裝置
    // EX:
    // {
    //   reportDateStart: "2022-09-17",
    //   reportDateEnd: "2022-10-17",
    //   carriers: [
    //     "mobile-ios",
    //     "mobile-android",
    //     "mobile-web",
    //     "desktop-web",
    //     "unknown"
    //   ]
    // }
    const paramsList = [{ reportDateStart: moment(start).format(DATE_FORMAT.API), reportDateEnd: moment(end).format(DATE_FORMAT.API), carriers: ALL_CARRIERS }]
    const [reqError, respList] = await safeAwait(
      Promise.all(_.map(paramsList, fetchAllListingPageViews))
    )

    if (reqError) {
      const error = _.get(reqError, 'payload.errorData.error[0]')
      const errorMessage = errorHelper.parseErrorMessage(error)
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }
    const pageViewsList = _.map(respList, resp => _.get(resp, 'data', []))
    const formValues = {
      carriers: [CARRIER.ALL],
      timeDuration: DEFAULT_TIME_DURATION
    }
    // 將全部區間的 pageViews 轉換成圖表資料格式
    // EX:
    //  array=[{
    //     carrier: "all",
    //     data:[{
    //         pageView: 8,
    //         correctReportDate: "2022-09-18",
    //         reportDate: "2022-09-18",
    //         source: [
    //           {
    //             carrier: "desktop-web",
    //             device: "desktop",
    //             pageView: 1,
    //             platform: "web-pc",
    //             referralGroup: "搜尋結果頁",
    //             referralType: "internal",
    //             reportDate: "2022-09-18"
    //           },
    //         ]
    //       }]
    //   }]

    const chartDataList = transformPageViewsListToChartDataList(pageViewsList, formValues)
    this.setIsSuccess(true)
    const convertedData = _.flow(
      () => _.get(payload, 'newOrder.statistics', []),
      statistics => _.map(statistics, stat => ({ ...stat, amount: +stat.amount }))
    )()
    const pageViewsData = _.get(chartDataList, '[0].[0].data', [])
    const chartData = mergeSaleData(convertedData, pageViewsData)
    this.setState({
      data: chartData
    })
  }

  componentDidMount () {
    const { periodStart, periodEnd } = this.props
    this.fetchData(periodStart, periodEnd)
  }

  componentDidUpdate (prevProps) {
    const { periodStart, periodEnd } = this.props
    if (`${prevProps.periodStart}${prevProps.periodEnd}` !== `${periodStart}${periodEnd}`) {
      this.setIsLoading(true)
      this.fetchData(periodStart, periodEnd)
    }
  }

  render () {
    const { data, isLoading } = this.state
    return (
      <Loader loaded={!isLoading}>
        {_.isEmpty(data)
          ? (
            <NoData iconType='history' />
          )
          : (
            <DailyChart data={data} />
          )
        }
      </Loader>
    )
  }
}

class DailyChart extends ComposeChartModule {
  constructor () {
    super()
    this.state = {}
  }

  render () {
    const { data } = this.props
    return (
      <div className={styles.chartBlock}>
        <TooltipIcon
          className={styles.infoTip}
          tooltipTitle='平均客單 = 訂單成立金額/訂單成立數'
        />
        <ReactResizeDetector
          handleWidth
          handleHeight
          onResize={this.updateChartWidth}
        />
        <ResponsiveContainer
          width='100%'
          height='100%'
          minHeight='250px'
        >
          <ComposedChart
            ref={this.initChart}
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }}
          >
            <XAxis
              tickLine={false}
              dataKey={FIELD.REPORT_DATE}
              tick={<CustomizeXAxisTick />}
              interval={1}
            />
            <YAxis
              yAxisId='left'
              axisLine={false}
              tickLine={false}
              tick={(
                <YAxisTick
                  prefix='$'
                  customizeTickValue={dollarFormat}
                  textAnchor='end'
                />
              )}
            >
              <Label
                value='金額'
                offset={35}
                position='left'
                angle={-90}
                content={<YAxisLabel />}
              />
            </YAxis>
            <YAxis
              yAxisId='right'
              orientation='right'
              axisLine={false}
              tickLine={false}
              domain={[0, this.calcMaxDomain()]}
              tick={(
                <YAxisTick
                  customizeTickValue={getNumeralFormat}
                  textAnchor='start'
                />
              )}
              allowDecimals
            >
              <Label
                value='數量'
                offset={30}
                position='right'
                angle={90}
                content={<YAxisLabel />}
              />
            </YAxis>
            <CartesianGrid
              vertical={false}
              stroke='#f5f5f5'
            />
            <Tooltip
              cursor={{
                stroke: '#eee',
                strokeWidth: this.state.tooltipCursorWidth
              }}
              content={(
                <QuantityAmountTooltip
                  format={DATE_FORMAT.UI}
                  isSaleDailyChart
                />
              )}
            />
            <Legend
              align='left'
              verticalAlign='top'
              wrapperStyle={{
                top: '-40px',
                color: '#9aa0ab'
              }}
              content={<CustomizedLegend />}
            />
            <Bar
              name='訂單成立金額'
              yAxisId='left'
              dataKey={FIELD.AMOUNT}
              legendType='square'
              fill='#6a96f3'
              barSize={this.state.barSize}
            />
            <Line
              name='訂單成立數'
              yAxisId='right'
              dataKey={FIELD.QUANTITY}
              stroke='#7dd5a9'
              strokeWidth='2'
              activeDot={false}
              dot={<LineDot />}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    )
  }
}

export default SaleDailyChartContainer
